import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'

/**
 * SSE — sohbette yeni mesaj/reaksiyon olunca backend realtime.bump('chat')
 * → /chat/stream 'update' → ilgili query'ler invalidate.
 * Sohbet hızlı olmalı: throttle 400ms (tasks 4sn). Kapsam: aktif kanal + kanal listesi + okunmamış.
 *
 * ⚠️ Bu hook YALNIZ sohbet sayfası / dock açıkken / belge paneli mount'luyken bağlanır.
 * Diğer her sayfada canlılık birleşik akıştan gelir (`useAppStream`, kanal `chat` +
 * `notif`) — o kanallar eklenmeden önce tek sinyal 60 sn'lik yoklamaydı.
 */
// enabled=false → HİÇ bağlanma. Tarayıcı aynı hosta sınırlı sayıda bağlantı açar
// (HTTP/1.1'de ~6) ve sohbet paneli + tam ekran + crm/mail/teklif stream'leri aynı
// kotayı paylaşır; kullanılmayan yerde stream açık tutmak diğerlerini aç bırakır.
// extraKey: JSON dizesi olarak ek sorgu anahtarı (dizi kimliği her render değişip
// bağlantıyı yeniden kurmasın diye dize) — belge görüşmesi henüz açılmamışken kanal sorgusu.
export function useChatStream(activeChannelId, threadRootId, enabled = true, extraKey = null) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    if (typeof EventSource === 'undefined') return
    let es
    let closed = false
    let reconnectTimer
    let pendingTimer
    let pollTimer
    let fails = 0
    let lastInvalidate = 0

    const doInvalidate = () => {
      lastInvalidate = Date.now()
      qc.invalidateQueries({ queryKey: ['chat-conversations'] })
      qc.invalidateQueries({ queryKey: ['chat-unread'] })
      if (activeChannelId) qc.invalidateQueries({ queryKey: ['chat-messages', activeChannelId] })
      if (threadRootId) qc.invalidateQueries({ queryKey: ['chat-thread', threadRootId] })
      if (extraKey) { try { qc.invalidateQueries({ queryKey: JSON.parse(extraKey) }) } catch { /* yut */ } }
    }
    // Arka uç zaten 1 sn'de bir yokluyor; üstüne 1 sn eşik binince mesaj ~2 sn
    // sonra düşüyordu. 400 ms art arda gelen mesajlarda hâlâ toplu invalidate sağlar.
    const THROTTLE_MS = 400
    const onUpdate = () => {
      const since = Date.now() - lastInvalidate
      if (since >= THROTTLE_MS) doInvalidate()
      else if (!pendingTimer) pendingTimer = setTimeout(() => { pendingTimer = null; doInvalidate() }, THROTTLE_MS - since)
    }

    const connect = () => {
      if (closed) return
      const token = useAuthStore.getState().token
      if (!token) { reconnectTimer = setTimeout(connect, 5000); return }
      try {
        es = new EventSource(`/api/v1/chat/stream?token=${encodeURIComponent(token)}`)
        // Bağlanınca bir kez tazele: ilk çekim ile akışın açılması arasında yazılan
        // mesaj için 'update' hiç gelmez (yeniden bağlanmada da aynı boşluk).
        es.addEventListener('ready', () => { fails = 0; stopPolling(); onUpdate() })
        es.addEventListener('update', onUpdate)
        es.onerror = () => {
          try { es.close() } catch { /* yut */ }
          fails += 1
          if (fails >= 2) startPolling()   // SSE tutmuyorsa (proxy/503) 10 sn'lik yoklamaya düş
          if (!closed) {
            clearTimeout(reconnectTimer)
            reconnectTimer = setTimeout(connect, Math.min(5000 * fails, 30000))
          }
        }
      } catch {
        startPolling()
      }
    }
    const startPolling = () => { if (!pollTimer && !closed) pollTimer = setInterval(doInvalidate, 10000) }
    const stopPolling = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null } }
    connect()

    return () => {
      closed = true
      clearTimeout(reconnectTimer)
      clearTimeout(pendingTimer)
      if (pollTimer) clearInterval(pollTimer)
      if (es) es.close()
    }
  }, [qc, activeChannelId, threadRootId, enabled, extraKey])
}
